import {
  Box,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { AnnouncementPicker } from 'components/shared/announcementPicker/announcementPicker';
import { QueuePicker, QueuePickerOption } from 'components/shared/queuePicker';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import type { FlowCallNodeVoicemail } from '../nodeTypes';

import styles from './voicemail.module.scss';

const ANNOUNCEMENT_FIELD = 'announcement';
const QUEUE_FIELD = 'queue';

type Props = FlowManagerEditModalComponentT<FlowCallNodeVoicemail>;

type Errors = {
  [Key in typeof ANNOUNCEMENT_FIELD | typeof QUEUE_FIELD]?: string;
};

export function VoicemailModal({
  node,
  nodeLabel,
  triggerClose,
  replaceNode,
}: Props): JSX.Element {
  const { payload } = node;
  const {
    announcement: { file: savedAnnouncement = null } = {},
    queue: savedQueue,
  } = payload || {};

  const [announcement, setAnnouncement] = useState<AnnouncementItem | null>(
    savedAnnouncement,
  );
  const [queue, setQueue] = useState(savedQueue);
  const [errors, setErrors] = useState<Errors>({});

  const isChanged = useIsChanged(
    [savedAnnouncement, announcement],
    [savedQueue, queue],
  );

  const validate = useCallback(() => {
    const errors: Errors = {};

    if (!announcement) {
      errors[ANNOUNCEMENT_FIELD] = 'Selecting an announcement is required';
    }

    if (!queue) {
      errors[QUEUE_FIELD] = 'Selecting a queue is required';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [queue, announcement, setErrors]);
  const handleSave = useCallback(() => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const payload: FlowCallNodeVoicemail = {
      ...node,
      icon: 'i-record-tape',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.VOICEMAIL,
      overlay: [],
      payload: {
        announcement: {
          file: announcement ?? null,
          tts: null,
        },
        queue: queue ?? null,
        transcription: true,
      },
      text: 'Voicemail',
    };

    replaceNode(payload);
    triggerClose();
  }, [announcement, queue, validate, triggerClose, replaceNode, node]);
  const onAnnouncementSelect = useCallback(
    (announcement: AnnouncementItem) => {
      setAnnouncement(announcement);
    },
    [setAnnouncement],
  );
  const onQueueSelect = useCallback(
    (queue?: QueuePickerOption) => {
      if (!queue) {
        return;
      }

      const { id: value, name: label } = queue;

      setQueue({ label, value });
    },
    [setQueue],
  );

  const announcementError = errors[ANNOUNCEMENT_FIELD];
  const queueError = errors[QUEUE_FIELD];

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={triggerClose} />
      <ModalBody>
        <Text.Paragraph subdued>
          Record or upload a voicemail asking your customers to leave a message
          if no agents are available.
        </Text.Paragraph>
        <FormGrid className={styles.form}>
          <FormField>
            <AnnouncementPicker
              adapter={AdapterTypes.Announcement}
              onSelect={onAnnouncementSelect}
              selectedAudio={announcement}
              fieldProps={{ meta: { error: announcementError } }}
            />
          </FormField>
          <FormField>
            <QueuePicker
              label="Select queue"
              placeholder="No queue selected"
              selectedQueue={queue}
              meta={{ error: queueError }}
              input={{
                name: 'queue',
                onChange: onQueueSelect,
              }}
              searchable
            />
          </FormField>
        </FormGrid>
      </ModalBody>
      <ModalFoot
        confirmOnClick={handleSave}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{ disabled: !isChanged }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
